import type { ReactNode, MouseEvent as ReactMouseEvent } from "react";
import { useRouterState } from "@tanstack/react-router";
import { MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics-events";
import { resolveRefCode } from "@/lib/attribution";
import { campaignPage, withRef } from "@/lib/campaign-pages";

export const SMS_NUMBER = "+16784317222";
export const SMS_DISPLAY = "(678) 431-7222";

const DEFAULT_MSG = "Hi Maria! I'd like to check availability. Date: __ Pickup: __ Group size: __";
const CORPORATE_MSG = "Hi Maria! I'm reaching out about corporate / private aviation transportation.";
const PAGE_MSGS: Record<string, string> = {
  "/uga-game-day-transportation": "Hi Maria! I'd like to check availability for a UGA game day. Game date: __ Group size: __ Pickup address: __",
  "/mercedes-benz-stadium-transportation": "Hi Maria! I'd like to check availability for Mercedes-Benz Stadium. Event & date: __ Group size: __ Pickup address: __",
  "/georgia-vs-florida-game-transportation": "Hi Maria! I'd like to check availability for Georgia vs. Florida. Date: __ Group size: __ Pickup address: __",
  "/corporate-accounts": "Hi Maria! I'm reaching out about corporate / private aviation transportation. Company: __ Date: __ Airport/FBO: __ Passengers: __",
  "/corporate-transportation-atlanta": CORPORATE_MSG,
  "/private-jet-car-service-atlanta": CORPORATE_MSG,
  "/fbo-transportation-atlanta": CORPORATE_MSG,
};

export function smsHref(message: string) {
  return `sms:${SMS_NUMBER}?&body=${encodeURIComponent(message)}`;
}

/** Pages where every booking CTA opens a text on all devices. */
const SMS_CTA_PAGES = new Set([
  "/uga-game-day-transportation",
  "/mercedes-benz-stadium-transportation",
  "/georgia-vs-florida-game-transportation",
  "/corporate-accounts",
]);

function usePath() {
  return useRouterState({ select: (s) => s.location.pathname }).replace(/\/+$/, "") || "/";
}

/** Campaign pages end every prefilled text with "Ref: <page default>" (SSR-safe; URL ref swapped in on click). */
export function useSmsMessage() {
  const path = usePath();
  const msg = PAGE_MSGS[path] ?? DEFAULT_MSG;
  const page = campaignPage(path);
  return page ? withRef(msg, page.ref) : msg;
}

/** On campaign pages, swap in the URL ref (if any) right before the text app opens. */
function applyLiveRef(e: ReactMouseEvent<HTMLAnchorElement>, msg: string, primary: boolean) {
  const page = campaignPage(window.location.pathname);
  if (!page) return;
  const ref_code = resolveRefCode();
  e.currentTarget.href = smsHref(withRef(msg, ref_code));
  // GA4 only: the document click listener already saves this tap to lead_events as sms_click.
  if (primary) trackEvent("text_us_click", { link_location: e.currentTarget.closest("[data-track-location]")?.getAttribute("data-track-location") ?? "page", ref_code }, { record: false });
}

/** On SMS-CTA pages returns the page's sms: link, otherwise null. */
export function useSmsCtaHref() {
  const path = usePath();
  const msg = useSmsMessage();
  return SMS_CTA_PAGES.has(path) ? smsHref(msg) : null;
}

function copyNumber() {
  const done = () => toast.success("Number copied. Text me anytime, Maria");
  try {
    navigator.clipboard.writeText(SMS_DISPLAY).then(done, done);
  } catch {
    done();
  }
  trackEvent("sms_copy");
}

/**
 * Text Us link. On phones/tablets (touch) renders an sms: link; on desktop
 * (fine pointer) renders plain copyable "Text us: (678) 431-7222" text.
 */
export default function TextUs({
  className,
  children,
  desktopClassName = "text-sm text-muted-foreground select-all",
  ariaLabel = "Text Maria at Atlanta Luxury Sprinters",
  primary = false,
}: {
  className?: string;
  children?: ReactNode;
  desktopClassName?: string;
  ariaLabel?: string;
  /** The page's main Text Us CTA: also fires text_us_click on campaign pages. */
  primary?: boolean;
}) {
  const msg = useSmsMessage();
  const ctaHref = useSmsCtaHref();
  if (ctaHref) {
    return (
      <a href={ctaHref} data-sms-cta className={className} aria-label={ariaLabel} onClick={(e) => applyLiveRef(e, msg, primary)}>
        {children ?? (
          <>
            <MessageSquareText className="w-4 h-4" aria-hidden="true" /> Text Maria
          </>
        )}
      </a>
    );
  }
  return (
    <>
      <a href={smsHref(msg)} className={`sms-touch-only ${className ?? ""}`} aria-label={ariaLabel} onClick={(e) => applyLiveRef(e, msg, primary)}>
        {children ?? (
          <>
            <MessageSquareText className="w-4 h-4" aria-hidden="true" /> Text Maria
          </>
        )}
      </a>
      <button
        type="button"
        data-sms-copy
        data-track-manual
        className={`sms-desktop-only cursor-pointer ${desktopClassName}`}
        onClick={copyNumber}
      >
        Text Maria: {SMS_DISPLAY} · Copy
      </button>
    </>
  );
}
